import * as React from 'react';
import styled from '@emotion/styled';

interface CallingScheduleSummaryProps {
    days: Array<{
        dayOfWeek: number;
        timeWindows?: string[];
        retryUnconfirmedTimeWindows?: string[];
    }>;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const formatWindow = (value: string) => value
    .replace(/:00(?=\s*[AP]M\b)/gi, '')
    .replace(/\s*(AM|PM)\b/gi, ' $1')
    .replace(/\s*[-–]\s*/g, ' – ');

const renderWindows = (windows?: string[]) => windows && windows.length > 0
    ? windows.map((window, index) => <TimeWindow key={index}>{formatWindow(window)}</TimeWindow>)
    : <EmptyWindow>None</EmptyWindow>;

const CallingScheduleSummary: React.FC<CallingScheduleSummaryProps> = ({ days }) => (
    <ScheduleContainer tabIndex={0} role="region" aria-label="Weekly calling schedule">
        <ScheduleTable aria-label="Calling schedule policy">
            <thead>
                <tr>
                    <th scope="col">Day</th>
                    <th scope="col">Calling windows</th>
                    <th scope="col">Retry windows</th>
                </tr>
            </thead>
            <tbody>
                {days.slice().sort((a, b) => a.dayOfWeek - b.dayOfWeek).map(day => (
                    <tr key={day.dayOfWeek}>
                        <th scope="row">{DAY_NAMES[day.dayOfWeek]}</th>
                        <td><WindowList>{renderWindows(day.timeWindows)}</WindowList></td>
                        <td><WindowList>{renderWindows(day.retryUnconfirmedTimeWindows)}</WindowList></td>
                    </tr>
                ))}
            </tbody>
        </ScheduleTable>
    </ScheduleContainer>
);

const ScheduleContainer = styled.div`
    margin-top: 8px;
    border: 1px solid #dbe4ef;
    border-radius: 8px;
    overflow-x: auto;
    &:focus-visible {
        outline: 2px solid #287f9c;
        outline-offset: 2px;
    }
`;

const ScheduleTable = styled.table`
    width: 100%;
    min-width: 480px;
    border-collapse: collapse;
    text-align: left;
    font-size: 13px;
    line-height: 20px;
    color: #334155;
    background: #fff;
    th, td {
        padding: 10px 12px;
        vertical-align: top;
    }
    thead {
        background: #edf3f7;
        color: #475569;
    }
    tbody tr + tr {
        border-top: 1px solid #e2e8f0;
    }
    tbody th {
        width: 110px;
        font-weight: 600;
    }
    td {
        width: 43%;
    }
`;

const WindowList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const TimeWindow = styled.span`
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    background: #f1f5f9;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
`;

const EmptyWindow = styled.span`
    color: #64748b;
`;

export default CallingScheduleSummary;
